import {
  FormHelperText,
  InputBase,
  inputBaseClasses,
  Stack,
  SxProps,
} from '@mui/material'
import { formHelperTextClasses } from '@mui/material/FormHelperText'
import { Fragment } from 'react'
import { RegisterOptions, useController, UseFormReturn } from 'react-hook-form'
import { ErrorIcon } from '../icons'

interface InputFieldProps {
  name: string
  form?: UseFormReturn<any, any>
  style?: {
    height: number
    borderRadius?: string
    width?: {
      xs: string | number
    }
    backgroundColor?: string
    [index: string]: any
  }
  needBorder?: boolean
  padding: string
  rules?: Exclude<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs'
  >
  type?: string | undefined
  placeholder?: string
  autoFocus?: boolean
  onChange?: any
  inputRef?: any
  min?: string
  max?: string
  endAdornment?: React.ReactNode
  disabled?: boolean
  value?: any
  login?: boolean
  sx?: any
  customStyleError?: {
    sx?: SxProps
  }
}

const InputField = ({
  name,
  form,
  placeholder,
  padding,
  type,
  onChange,
  style,
  inputRef,
  min,
  max,
  endAdornment,
  disabled,
  value,
  login,
  needBorder = true,
  sx,
  customStyleError,
  ...props
}: InputFieldProps) => {
  const { control } = form || { control: null }

  const {
    field,
    formState: { errors },
  } = control
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useController({ name, control })
    : {
        field: { onChange, value, onBlur: undefined, ref: undefined },
        formState: { errors: undefined },
      }

  return (
    <Fragment>
      <InputBase
        autoComplete="off"
        fullWidth
        sx={{
          gap: '4px',
          border: needBorder ? '1px solid #F1F1F1' : 'none',
          lineHeight: '115%',
          '&:hover': {
            backgroundColor: !!errors?.[name]
              ? 'error.main'
              : login
              ? '#FFF'
              : 'background.overrideDefault',
          },
          [`& .${inputBaseClasses.input}`]: {
            padding: { padding },
            height: '100%',
            borderRadius: '4px',
            MozAppearance: 'textfield',
            color: login ? '#000' : 'text.main',
          },
          [`&.${inputBaseClasses.focused}`]: {
            border: '1px solid #F1F1F1',
            color: 'text.main',
            backgroundColor: !!errors?.[name]
              ? 'error.main'
              : login
              ? '#FFF'
              : 'background.overrideDefault',
          },
          [`& .${inputBaseClasses.input}::placeholder`]: {
            opacity: 1,
            color: !!errors?.[name] ? '#000' : 'text.main',
          },
          color: !!errors?.[name] ? '#000' : 'text.main',
          backgroundColor: !!errors?.[name]
            ? 'error.main'
            : login
            ? '#FFF'
            : 'background.overrideDefault',
          ...sx,
          ...style,
          width: style?.width?.xs || '100%',
        }}
        id={name}
        type={type}
        inputProps={{ ...field, min, max, value: field.value ?? '' }}
        onChange={(e) =>
          onChange ? field.onChange(onChange(e)) : field.onChange(e)
        }
        {...props}
        placeholder={placeholder}
        inputRef={inputRef}
        endAdornment={endAdornment}
        disabled={disabled}
      />

      {errors?.[name]?.message && (
        <Stack direction="row" spacing="5px" mt="3px" color="#FF3030">
          <ErrorIcon />
          <FormHelperText
            id="component-helper-text"
            error
            sx={{
              fontSize: '12px',
              fontWeight: '400',
              lineHeight: '115%',
              flex: 'none',
              letterSpacing: '0.045em',
              [`&.${formHelperTextClasses.error}`]: {
                color: 'error.main',
                marginLeft: '9px',
                marginTop: '1px',
              },
              ...customStyleError?.sx,
            }}
          >
            {errors?.[name]?.message as string}
          </FormHelperText>
        </Stack>
      )}
    </Fragment>
  )
}

export default InputField
